/**
 * **試合 1 つぶんの素材を読む**（Issue #637。`/duel-timing` と `/duel-editor` の共通。`useDuelStage` が使う）。
 *
 * 読み込みはページの仕事（`duelEditor/` は `api` も `ocgCore` も知らない）なので、ここは `pages/` に置く。
 * 試合を渡すと 詳細（動画の URL・デッキ）→ config のゾーン → replay の盤面 → カードの画像 を順に取る。
 *
 * **戻り値の形が `useDuelStage` の `match` の形**（Issue #766）。外から同じ形を作って `useDuelStage({ match })` に渡すこともできる。
 *
 * **`crossOrigin` は必須**（Issue #523）—— 盤の紙とオブジェクトの板は canvas に描いてから WebGL の
 * テクスチャにするので、汚れた canvas は `texImage2D` で SecurityError になり**カードだけ消える**。
 * 本番の画像 CloudFront は CORS 付きで返す。localhost の dev サーバーからは弾かれるので、
 * 手元で絵を見るときは Chrome を `--disable-web-security` で起動する。
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/api';
import { deckIdsOfGame, playersFromGame } from '../utils/gameInfo';
import { latestReplayId, listReplays, loadReplay, replayHead } from '../components/ocgCore';
import { useCardAccess } from './useCardAccess';

/**
 * @param gameUuid 試合（空なら何も読まない）
 * @param replayId 開く replay（null なら一番新しいもの）
 */
export function useDuelMatch(gameUuid, replayId = null) {
  const [gameInfo, setGameInfo] = useState(null);
  const [fieldGenConfig, setFieldGenConfig] = useState(null);
  const [loaded, setLoaded] = useState(null);       // loadReplay の結果（state / replay）
  const [loadedId, setLoadedId] = useState(null);   // 開いた replay の id
  const [cards, setCards] = useState({});           // code → { name, url }
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gameUuid) { setGameInfo(null); setFieldGenConfig(null); setLoaded(null); setLoadedId(null); setCards({}); return undefined; }
    let alive = true;
    setError(null); setNote(null);
    (async () => {
      try {
        const info = await api.project('GetDuelGameInfo', { game_uuid: gameUuid });
        const game = info?.status === 'success' ? info.duel : null;
        if (!alive) return;
        if (!game) { setError('試合が見つかりません'); return; }
        setGameInfo(game);
        Promise.resolve(api.project('GetFieldGenConfig', { game_id: game.id }))
          .then((res) => { if (alive) setFieldGenConfig(res?.config ?? null); }).catch(() => {});
        Promise.all(deckIdsOfGame(game).map((id) => (id ? api.project('GetDeckCards', { deck_id: id }) : null)))
          .then((results) => {
            if (!alive) return;
            const index = {};
            results.forEach((res) => (res?.cards ?? []).forEach((c) => {
              index[String(c.card_id)] = { name: c.card_name, url: c.download_url ?? c.thumbnail_url ?? null };
            }));
            setCards(index);
          }).catch(() => {});
        const id = replayId ?? latestReplayId(await listReplays(gameUuid));
        if (!id) { if (alive) setNote('この試合には再現エンジンの replay がありません（カードは出ません）'); return; }
        const got = await loadReplay(id);
        if (alive) { setLoaded(got); setLoadedId(id); }
      } catch (e) { if (alive) setError(String(e?.message ?? e)); }
    })();
    return () => { alive = false; };
  }, [gameUuid, replayId]);

  // プレイヤーの情報（名前・色・デッキ）は**試合の属性**（時間で変わらない）。盤も HUD も読む
  const players = useMemo(() => playersFromGame(gameInfo), [gameInfo]);

  // カードの絵・名前・スリーブの引き方（描く側の演出ページと共通。Issue #766）
  const { cardAccess, cardName, cardsLoaded } = useCardAccess(cards, players);

  // 動画（生成済みフィールド動画。低画質＝動画1 を優先）
  const movieUrl = useMemo(() => {
    for (const i of [1, 2, 3]) if (gameInfo?.[`movie${i}_download_url`]) return gameInfo[`movie${i}_download_url`];
    return null;
  }, [gameInfo]);

  const head = useMemo(() => replayHead(loaded?.replay), [loaded]);

  return {
    gameInfo, fieldGenConfig, loaded, replayId: loadedId, head, players, movieUrl, cards, cardAccess, cardName, cardsLoaded, note, error,
  };
}
